import Text from '../../../../../elements/Typo/Text'
import React, { useEffect, useRef, useState } from 'react'
import { CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useTranslation } from 'react-i18next'

interface Props {
    title: string
    data: any
    groups: any[] | null
    className?: string
}

const graphColors: any = ['#E5C511', '#D69E77', '#c2c2c2', '#D69E77', '#E5C511']

const CustomTooltip = ({ active, payload, groups }: any) => {
    if (active && payload?.length) {
        return (
            <div className="bg-white shadow-lg rounded-[0.667rem] p-3">
                {groups?.map((group: any, i: any) => (
                    <div className="flex items-center gap-2" key={`tooltip-${group.name}-${i}`}>
                        <div className="w-[0.583rem] h-[0.583rem] rounded-[0.167rem]" style={{ background: graphColors[i] }}></div>
                        <Text size="sm" className="flex-grow text-[#74788D] mr-4">
                            {group.name}
                        </Text>
                        <Text size="sm" className="font-semibold">
                            {payload[0].payload[group.name]}
                        </Text>
                    </div>
                ))}
            </div>
        )
    }

    return null
}

const LineData: React.FC<Props> = ({ title = '', data, groups, className = '' }) => {
    const { t } = useTranslation()
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)

    const renderCustomizedLabel = (props: any) => {
        return <text>{t('Hi')}</text>
    }

    const [zoomArea, setZoomArea] = useState<{
        data: any[]
        refAreaLeft: string
        refAreaRight: string
        top: number
        left: number
        right: number
        bottom: number
        xAxis: any
        yAxis: any
    }>({
        data: data.data ?? [],
        refAreaLeft: '',
        refAreaRight: '',
        top: 0,
        bottom: 0,
        left: 0,
        right: data.data.length,
        xAxis: data.xAxis,
        yAxis: data.yAxis,
    })

    const [containerWidth, setContainerWidth] = useState(0)
    const [chartWidth, setChartWidth] = useState(0)
    const gapBetweenXaxisLabels = 15

    const getTicksOfAxis = (yAxis: any) => {
        const max = Math.ceil(yAxis.max / 10) * 10
        return Array.from({ length: 10 + 1 }, (_, i) => Math.ceil((i * max) / 10 / 10) * 10)
    }

    const selectZoneAndZoomIn = () => {
        if (data?.data) {
            const { left, right } = zoomArea
            const middle = Math.floor((left + right) / 2)

            const currentRange = right - left

            // Zoom in by 20% from each side (40% total zoom)
            const zoomPercentage = 0.2
            const shiftingBy = Math.max(1, Math.floor(currentRange * zoomPercentage))

            const updatedLeft = Math.min(middle, left + shiftingBy)
            const updatedRight = Math.max(middle, right - shiftingBy)
            const selectedData = data.data.slice(updatedLeft, updatedRight)

            if (selectedData.length) {
                setZoomArea({
                    ...zoomArea,
                    data: selectedData,
                    refAreaLeft: '',
                    refAreaRight: '',
                    left: updatedLeft,
                    right: updatedRight,
                })
            }
        }
    }

    const handleMouseUp = () => {
        if (data?.data) {
            let { refAreaLeft, refAreaRight } = zoomArea
            if (refAreaLeft === refAreaRight || refAreaRight === '') {
                setZoomArea((prevState: any) => ({
                    ...prevState,
                    refAreaLeft: '',
                    refAreaRight: '',
                }))
                return
            }
            const indx = data?.data?.findIndex((i: any) => i.Date === refAreaLeft)
            const indx2 = data?.data?.findIndex((i: any) => i.Date === refAreaRight)

            if (indx > indx2) [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft]
            zoomOut()
            setZoomArea((prevState: any) => ({
                ...prevState,
                data: data.data.slice(indx, indx2 + 1),
                refAreaLeft: '',
                refAreaRight: '',
                left: indx,
                right: indx2,
            }))
        }
    }

    const zoomOut = () => {
        setZoomArea({
            data: data.data ?? [],
            refAreaLeft: '',
            refAreaRight: '',
            top: 0,
            bottom: 0,
            left: 0,
            right: data.data.length,
            xAxis: data.xAxis,
            yAxis: data.yAxis,
        })
    }

    const handleMouseDown = (e: any) => {
        if (e?.activeLabel) {
            setZoomArea((prevState: any) => ({
                ...prevState,
                refAreaLeft: e.activeLabel ?? '',
            }))
        }
    }

    const calculateRequiredWidth = () => {
        const dataCount = zoomArea.data.length

        // Calculate the total width needed for all bars without overlapping
        const requiredWidth = dataCount * gapBetweenXaxisLabels
        setChartWidth(requiredWidth)
    }

    useEffect(() => {
        if (scrollContainerRef.current) {
            // Get the width of the parent container
            setContainerWidth(scrollContainerRef.current.clientWidth)
        }

        // Add resize listener
        const handleResize = () => {
            if (scrollContainerRef.current) {
                setContainerWidth(scrollContainerRef.current.clientWidth)
            }
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        calculateRequiredWidth()
    }, [containerWidth, zoomArea.data])

    return (
        <div className={`border border-[#0000001A] rounded-[0.625rem] p-2 mt-8 overflow-x-scroll graph-container`} ref={scrollContainerRef}>
            <div className="flex items-center justify-between mb-2">
                <Text size="lg" className="font-semibold">
                    {t('Distribution by')} {title}
                </Text>
                <div>
                    <div className="flex items-center gap-4">
                        {groups?.map((group: any) => {
                            const uniqueKey = `graph-legend-${group.name || JSON.stringify(group)}`
                            return (
                                <div className="flex items-center gap-2" key={uniqueKey}>
                                    <div className="w-[0.583rem] h-[0.583rem] rounded-[0.167rem]" style={{ background: graphColors[uniqueKey] }} />
                                    <Text size="md">{group.name}</Text>
                                </div>
                            )
                        })}
                    </div>
                    <div className="flex items-center justify-between mt-1 shadow-1 rounded-md">
                        <button type="button" className="text-center w-1/2 cursor-pointer hover:bg-[#F5F5F5] rounded-e" onClick={zoomOut} title={t('Reset Zoom')}>
                            <span className="font-normal text-nowrap">-</span>
                        </button>
                        <button type="button" className="border-[#D9D9D9] border-r text-center w-1/2 cursor-pointer hover:bg-[#F5F5F5] rounded-s" title={t('Zoom In')} onClick={selectZoneAndZoomIn}>
                            <span className="font-normal text-nowrap">+</span>
                        </button>
                    </div>
                    {/* <Button className="rounded-xl text-[.5rem] w-[4.5rem] mt-1" onClick={() => zoomOut()}>
                        <span className="font-normal text-[.6rem] text-nowrap">Zoom Out</span>
                    </Button> */}
                </div>
            </div>
            <div className="w-full h-[20.542rem] text-[0.5rem] flex relative" style={{ width: `${chartWidth}px`, minWidth: '100%' }}>
                <div className="w-[1rem] flex items-center justify-center">
                    <p className="text-[0.5rem] text-center leading-none font-semibold -rotate-90 whitespace-nowrap">{title}</p>
                </div>

                <ResponsiveContainer width="100%" height="100%" className="flex-grow">
                    <LineChart
                        width={chartWidth}
                        data={zoomArea?.data}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 85,
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseMove={(e) => {
                            if (zoomArea.refAreaLeft) {
                                setZoomArea((prevState: any) => ({
                                    ...prevState,
                                    refAreaRight: e?.activeLabel ?? '',
                                }))
                            }
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey={zoomArea.xAxis.title} stroke="#939393" tickLine={false} interval={0} angle={-90} textAnchor="end" domain={[zoomArea?.left, zoomArea?.right]} />
                        <YAxis overlineThickness={1} domain={[zoomArea.bottom, zoomArea.top]} tickLine={false} axisLine={false} ticks={getTicksOfAxis(zoomArea.yAxis)} label={renderCustomizedLabel} width={25} />
                        <Tooltip content={<CustomTooltip groups={groups} />} cursor={{ fill: 'transparent' }} />
                        {groups?.map((gr: any) => {
                            const uniqueKey = `line-${gr.name || JSON.stringify(gr)}`
                            return <Line key={uniqueKey} type="monotone" dataKey={gr.name} stroke={graphColors[uniqueKey]} activeDot={{ r: 8 }} dot={false} />
                        })}

                        {zoomArea?.refAreaLeft && zoomArea.refAreaRight ? <ReferenceArea x1={zoomArea?.refAreaLeft} x2={zoomArea?.refAreaRight} strokeOpacity={0.3} /> : null}
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <p className="text-[0.5rem] text-center leading-none font-semibold  whitespace-nowrap">{zoomArea.xAxis.title}</p>
        </div>
    )
}

export default LineData
